// src/constants/propertyUpload.js
// Límites y constantes espejadas del backend (ver PROPERTY_UPLOAD_ERROR_HANDLING_FRONTEND.md §4).
// Sirven para validar en el cliente ANTES de subir y para mostrar mensajes coherentes con el server.

export const UPLOAD_LIMITS = {
  MAX_FILE_SIZE_BYTES: 30 * 1024 * 1024, // 30 MB por archivo
  MAX_TOTAL_PAYLOAD_BYTES: 50 * 1024 * 1024, // 50 MB total por request
  MAX_PHOTOS: 30,
  MIN_PHOTOS: 5,
  MAX_DOCUMENTS: 10,
  TITLE_MAX_LENGTH: 255,
  DESCRIPTION_MAX_LENGTH: 3000,
  // Timeout de request (2 min): coincide con el backend en cargas pesadas
  REQUEST_TIMEOUT_MS: 120000,
};

export const ALLOWED_PHOTO_MIMES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/svg+xml',
];

export const ALLOWED_DOC_MIMES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

// Descripciones legibles de los tipos permitidos (para mensajes de UI)
export const ALLOWED_PHOTO_LABEL = 'JPG, PNG, WEBP, GIF, SVG';
export const ALLOWED_DOC_LABEL = 'PDF, DOC, DOCX, XLS, XLSX';

// Helper para mostrar tamaños
export const bytesToMB = (b) => (b / (1024 * 1024)).toFixed(1);

// ============================================================
// Guía de errores para el instructivo (espejo del README §2).
// Cada fila explica qué significa el error y qué debe hacer el usuario.
// ============================================================
export const UPLOAD_ERROR_GROUPS = [
  {
    group: 'Conexión y red',
    rows: [
      {
        code: 'NETWORK_ERROR',
        type: 'NETWORK',
        cause: 'Sin internet, servidor inalcanzable o bloqueo de CORS. La solicitud nunca llegó.',
        action: 'Revisá tu conexión a internet y volvé a intentar con el botón "Reintentar".',
      },
      {
        code: 'REQUEST_ABORTED',
        type: 'NETWORK',
        cause: 'Cancelaste la subida o saliste de la página durante el envío.',
        action: 'No es un error real. Si querés continuar, volvé a enviar el formulario.',
      },
      {
        code: 'REQUEST_TIMEOUT',
        type: 'TIMEOUT',
        cause: 'El envío tardó más de 2 minutos (imágenes muy pesadas o red lenta).',
        action: 'Reducí el peso o la cantidad de fotos y reintentá.',
      },
    ],
  },
  {
    group: 'Tamaño y cantidad de archivos',
    rows: [
      {
        code: 'FILE_TOO_LARGE',
        type: 'UPLOAD',
        cause: 'Una imagen o documento supera los 30 MB permitidos.',
        action: 'Comprimí o reemplazá ese archivo por uno más liviano (máx. 30 MB).',
      },
      {
        code: 'PAYLOAD_TOO_LARGE',
        type: 'PAYLOAD_TOO_LARGE',
        cause: 'La suma de todos los archivos supera los 50 MB.',
        action: 'Subí menos archivos por vez o reducí su peso total.',
      },
      {
        code: 'TOO_MANY_PHOTOS',
        type: 'VALIDATION',
        cause: 'Cargaste más de 30 fotos.',
        action: 'Quitá fotos hasta quedar en 30 o menos.',
      },
      {
        code: 'TOO_MANY_DOCUMENTS',
        type: 'VALIDATION',
        cause: 'Cargaste más de 10 documentos.',
        action: 'Quitá documentos hasta quedar en 10 o menos.',
      },
      {
        code: 'PHOTO_REQUIRED',
        type: 'VALIDATION',
        cause: 'No cargaste el mínimo de fotos requerido.',
        action: 'Subí al menos 5 fotos de la propiedad.',
      },
    ],
  },
  {
    group: 'Tipo de archivo',
    rows: [
      {
        code: 'INVALID_FILE_TYPE',
        type: 'UPLOAD',
        cause: 'El formato del archivo no está permitido.',
        action: 'Fotos: JPG, PNG, WEBP, GIF, SVG. Documentos: PDF, DOC, DOCX, XLS, XLSX.',
      },
    ],
  },
  {
    group: 'Campos obligatorios',
    rows: [
      {
        code: 'PROPERTY_TYPE_REQUIRED',
        type: 'VALIDATION',
        cause: 'No elegiste el tipo de propiedad.',
        action: 'Seleccioná un tipo (casa, departamento, etc.).',
      },
      {
        code: 'TITLE_REQUIRED',
        type: 'VALIDATION',
        cause: 'Falta el título.',
        action: 'Escribí un título descriptivo (mínimo 3 caracteres).',
      },
      {
        code: 'PRICE_REQUIRED',
        type: 'VALIDATION',
        cause: 'Falta el precio.',
        action: 'Ingresá el precio (solo números enteros).',
      },
      {
        code: 'COUNTRY_REQUIRED / PROVINCE_REQUIRED',
        type: 'VALIDATION',
        cause: 'Falta país o provincia.',
        action: 'Completá la ubicación en la sección correspondiente.',
      },
      {
        code: 'LOCATION_REQUIRED',
        type: 'VALIDATION',
        cause: 'No marcaste la ubicación en el mapa.',
        action: 'Buscá la dirección o hacé click sobre el mapa para fijar el pin.',
      },
      {
        code: 'PROPERTY_STATE_REQUIRED',
        type: 'VALIDATION',
        cause: 'Falta el estado de la propiedad.',
        action: 'Elegí un estado (a estrenar, excelente, etc.).',
      },
    ],
  },
  {
    group: 'Formato y valores inválidos',
    rows: [
      {
        code: 'INVALID_NUMBER / INVALID_INTEGER_FORMAT',
        type: 'VALIDATION',
        cause: 'Se escribió texto donde iba un número.',
        action: 'Revisá superficies, ambientes, precio y antigüedad: solo números.',
      },
      {
        code: 'NEGATIVE_VALUE',
        type: 'VALIDATION',
        cause: 'Se ingresó un valor negativo.',
        action: 'Usá valores mayores o iguales a 0.',
      },
      {
        code: 'OUT_OF_RANGE',
        type: 'VALIDATION',
        cause: 'Un valor está fuera del rango permitido (ej. comisión 0–999.99).',
        action: 'Ajustá el valor dentro del rango indicado.',
      },
      {
        code: 'INVALID_EMAIL',
        type: 'VALIDATION',
        cause: 'El email del propietario no es válido.',
        action: 'Verificá el formato del email (ejemplo@dominio.com).',
      },
      {
        code: 'TITLE_TOO_LONG / DESCRIPTION_TOO_LONG',
        type: 'VALIDATION',
        cause: 'El título (>255) o la descripción (>3000) superan el máximo.',
        action: 'Acortá el texto para respetar los límites.',
      },
      {
        code: 'INVALID_DATE_FORMAT',
        type: 'VALIDATION',
        cause: 'La fecha no tiene el formato correcto.',
        action: 'Elegí la fecha desde el calendario.',
      },
    ],
  },
  {
    group: 'Servidor',
    rows: [
      {
        code: 'PROPERTY_NOT_FOUND',
        type: 'NOT_FOUND',
        cause: 'El recurso ya no existe.',
        action: 'Actualizá la página; la propiedad pudo haber sido eliminada.',
      },
      {
        code: 'INTERNAL_SERVER_ERROR',
        type: 'SERVER',
        cause: 'Error inesperado del servidor.',
        action: 'Reintentá más tarde y reportá el número de referencia (requestId) a soporte.',
      },
    ],
  },
];

